<script setup lang="ts">
import Button from 'primevue/button';
import { Section, SplitBar, Toolbar, Typography } from '@apparatix/library/core';
import { button as greenButtonTheme } from '@apparatix/theme/Green';
import TrashIcon from '@primeicons/vue/trash';
import PenToSquareIcon from '@primeicons/vue/pen-to-square';
import SpinnerIcon from '@primeicons/vue/spinner';

type Props = {
  isEditing?: boolean;
  isLoading?: boolean;
  disableEdit?: boolean;
  disableSave?: boolean;
  header?: string;
  hideButton?: boolean;
  enableDelete?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  header: 'Details',
});

const emit = defineEmits<{
  (e: 'on:edit-click'): void;
  (e: 'on:save-click'): void;
  (e: 'on:cancel-click'): void;
  (e: 'on:delete-click'): void;
}>();
</script>
<template>
  <Section :footer="props.isEditing" sticky-footer>
    <template #title>
      <SplitBar class="pb-3">
        <template #left>
          <Typography variant="section-heading">
            {{ props.header }}
          </Typography>
        </template>
        <template #right>
          <Toolbar align="right">
            <Button
              v-if="!props.hideButton && props.enableDelete"
              severity="danger"
              variant="outlined"
              :disabled="props.isEditing || props.isLoading || props.disableEdit"
              @click="emit('on:delete-click')"
            >
              <TrashIcon />
              Delete
            </Button>
            <Button
              v-if="!props.hideButton"
              variant="outlined"
              :disabled="props.isEditing || props.isLoading || props.disableEdit"
              @click="emit('on:edit-click')"
            >
              <PenToSquareIcon />
              {{ props.isEditing ? 'Editing' : 'Edit' }}
            </Button>
          </Toolbar>
        </template>
      </SplitBar>
    </template>
    <slot />
    <template v-if="props.isEditing" #footer>
      <Toolbar align="right" class="w-full">
        <Button
          severity="secondary"
          :disabled="props.isLoading"
          @click="emit('on:cancel-click')"
        >
          Cancel
        </Button>
        <Button
          :dt="greenButtonTheme"
          :disabled="props.disableSave || props.isLoading"
          @click="emit('on:save-click')"
        >
          <SpinnerIcon v-if="props.isLoading" spin />
          {{ props.isLoading ? 'Saving' : 'Save' }}
        </Button>
      </Toolbar>
    </template>
  </Section>
</template>
